import React, { useRef, useState } from 'react';
import { Palette, Upload, Loader2, X } from 'lucide-react';
import { BasicInfo, Customization } from '../types';
import { invitationTemplates, COVER_IMAGE_OPTIONS } from '../data';
import { storageService } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface Step3Props {
  basicInfo: BasicInfo;
  customization: Customization;
  selectedTemplate: number | null;
  updateBasicInfo: (field: string, value: any) => void;
  setCustomization: React.Dispatch<React.SetStateAction<Customization>>;
}

export const Step3_Customization: React.FC<Step3Props> = ({
  basicInfo,
  customization,
  selectedTemplate,
  updateBasicInfo,
  setCustomization,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const isUploadedImage = customization.coverImage &&
    !COVER_IMAGE_OPTIONS.includes(customization.coverImage);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('圖片大小不能超過 5MB');
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUploadError('僅支援 JPG、PNG、WebP 格式');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const publicUrl = await storageService.uploadCoverImage(file, user.id);
      setCustomization(prev => ({ ...prev, coverImage: publicUrl }));
    } catch {
      setUploadError('上傳失敗，請稍後再試');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold mb-6 flex items-center">
        <Palette className="h-6 w-6 text-pink-500 mr-3" />
        客製化設定 (選填)
      </h3>

      <div className="space-y-6">
        {selectedTemplate !== null && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              範本配色
            </label>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
              <div
                className="w-6 h-6 rounded-full border border-gray-300"
                style={{ backgroundColor: customization.primaryColor }}
              />
              <span className="text-sm text-gray-600">
                {invitationTemplates.find(t => t.id === selectedTemplate)?.name} 主題色彩
              </span>
            </div>
          </div>
        )}

        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">活動安排</h4>
          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={basicInfo.hasWelcomeParty}
                  onChange={(e) => updateBasicInfo('hasWelcomeParty', e.target.checked)}
                  className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-900">迎賓</span>
              </label>
              {basicInfo.hasWelcomeParty && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">時間</label>
                    <input
                      type="time"
                      value={basicInfo.welcomePartyTime}
                      onChange={(e) => updateBasicInfo('welcomePartyTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">地點（如與主要地點不同）</label>
                    <input
                      type="text"
                      value={basicInfo.welcomePartyVenue}
                      onChange={(e) => updateBasicInfo('welcomePartyVenue', e.target.value)}
                      placeholder="留空表示與主要地點相同"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={basicInfo.hasWeddingCeremony}
                  onChange={(e) => updateBasicInfo('hasWeddingCeremony', e.target.checked)}
                  className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-900">證婚儀式</span>
              </label>
              {basicInfo.hasWeddingCeremony && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">時間</label>
                    <input
                      type="time"
                      value={basicInfo.ceremonyTime}
                      onChange={(e) => updateBasicInfo('ceremonyTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">地點（如與主要地點不同）</label>
                    <input
                      type="text"
                      value={basicInfo.ceremonyVenue}
                      onChange={(e) => updateBasicInfo('ceremonyVenue', e.target.value)}
                      placeholder="留空表示與主要地點相同"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={basicInfo.hasReception}
                  onChange={(e) => updateBasicInfo('hasReception', e.target.checked)}
                  className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-900">婚宴</span>
              </label>
              {basicInfo.hasReception && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">時間</label>
                    <input
                      type="time"
                      value={basicInfo.receptionTime}
                      onChange={(e) => updateBasicInfo('receptionTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">地點（如與主要地點不同）</label>
                    <input
                      type="text"
                      value={basicInfo.receptionVenue}
                      onChange={(e) => updateBasicInfo('receptionVenue', e.target.value)}
                      placeholder="留空表示與主要地點相同"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">賓客服務選項</h4>
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={basicInfo.showTransportInfo}
                  onChange={(e) => updateBasicInfo('showTransportInfo', e.target.checked)}
                  className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-900">提供交通資訊</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 pl-7">在邀請函中顯示如何到達婚禮場地的交通資訊</p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={basicInfo.showContactInfo}
                  onChange={(e) => updateBasicInfo('showContactInfo', e.target.checked)}
                  className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-900">提供聯絡方式</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 pl-7">讓賓客能夠聯絡您詢問相關問題</p>
              {basicInfo.showContactInfo && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">聯絡電話</label>
                    <input
                      type="tel"
                      value={basicInfo.contactPhone}
                      onChange={(e) => updateBasicInfo('contactPhone', e.target.value)}
                      placeholder="例如：0912-345-678"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">聯絡 Email</label>
                    <input
                      type="email"
                      value={basicInfo.contactEmail}
                      onChange={(e) => updateBasicInfo('contactEmail', e.target.value)}
                      placeholder="例如：wedding@example.com"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            歡迎訊息
          </label>
          <textarea
            value={customization.welcomeMessage}
            onChange={(e) => setCustomization(prev => ({ ...prev, welcomeMessage: e.target.value }))}
            placeholder="親愛的朋友們，我們很榮幸邀請您參加我們的婚禮慶典..."
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">這段文字會顯示在邀請函的歡迎區域</p>
        </div>

        {basicInfo.showTransportInfo && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              交通資訊詳細內容
            </label>
            <textarea
              value={basicInfo.transportDetails}
              onChange={(e) => updateBasicInfo('transportDetails', e.target.value)}
              placeholder="請詳細說明如何到達婚禮場地的交通方式..."
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">留空將使用預設的交通資訊範本</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            封面圖片
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {COVER_IMAGE_OPTIONS.map((imageUrl, index) => (
              <div
                key={index}
                onClick={() => setCustomization(prev => ({ ...prev, coverImage: imageUrl }))}
                className={`aspect-video rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                  customization.coverImage === imageUrl
                    ? 'border-pink-500'
                    : 'border-gray-200 hover:border-pink-300'
                }`}
              >
                <img
                  src={imageUrl}
                  alt={`封面選項 ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          <div className="mt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileUpload}
              className="hidden"
            />

            {isUploadedImage ? (
              <div className="relative">
                <div className="aspect-video rounded-lg overflow-hidden border-2 border-pink-500 max-w-xs">
                  <img
                    src={customization.coverImage}
                    alt="自訂封面"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCustomization(prev => ({ ...prev, coverImage: COVER_IMAGE_OPTIONS[0] }));
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="text-xs text-green-600 mt-1">已使用自訂照片</p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-pink-400 hover:text-pink-600 transition-colors w-full justify-center"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    上傳中...
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5" />
                    上傳自己的婚紗照 / 合照
                  </>
                )}
              </button>
            )}

            {uploadError && (
              <p className="text-xs text-red-500 mt-1">{uploadError}</p>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-2">支援 JPG、PNG、WebP，最大 5MB</p>
        </div>
      </div>
    </div>
  );
};
